// Desktop shell sidebar (JW2, web/plan.md §Layout concept). Fills the
// AppShell `sidebar` slot at the desktop breakpoint: a header (wordmark +
// signaling dot, add-friend, settings), a search field, and one scrolling list
// with three sections — Favorites, Contacts (presence-sorted) and Recents
// (with the unseen-missed badge). It merges what used to be the Main and
// Contacts tabs onto one surface.
//
// Strictly reuses MainScreen's data paths — cached friends (+ useServerReload
// refresh), device-local favorites and call log, live presence — so it makes
// no API call MainScreen doesn't already make. Row click selects (navigates to
// the contact, which the right pane renders); the hover/focus call shortcut
// dials via the shared useDialer; the list is arrow-key navigable.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import type { Friend, PresenceStatus } from '@merkury/protocol';
import type { SignalingStatus } from '@merkury/brain/call/types.js';
import { MercuryAvatar } from '../../avatar/MercuryAvatar.js';
import { candidateSeed } from '../../avatar/mercury.js';
import { useT, type MessageKey, type TFunc } from '../../i18n/index.js';
import { appUpdateStore, updateIsWaiting } from '../../shell/appUpdate.js';
import {
  getCachedBlocks,
  getCachedFriends,
  setCachedBlocks,
  setCachedFriends,
} from '../../storage/serverCache.js';
import { markAllMissedSeen } from '../../storage/kvStores.js';
import { presenceOf } from '@merkury/brain/api/liveEvents.js';
import { useScreens } from '../ScreensContext.js';
import { InstallHint } from './InstallHint.js';
import { useStore } from './useStore.js';
import { useFavorites } from './useFavorites.js';
import { useCallLog } from './useCallLog.js';
import { useDialer } from './useDialer.js';
import { useServerReload } from './useServerReload.js';
import { describeCall, formatTimestamp } from './callFormat.js';
import { displayNameFor, effectiveName } from './peerName.js';
import { useAliases } from './useAliases.js';
import { buildSidebar } from './SidebarModel.js';

// Same mapping as MainScreen's SIGNALING_DOT (docs/06 §5): green connected,
// yellow racing/reconnecting, red down or revoked.
const SIGNALING_DOT: Record<SignalingStatus, { color: string; labelKey: MessageKey }> = {
  idle: { color: 'bg-bad', labelKey: 'main.signalingDown' },
  connecting: { color: 'bg-warn', labelKey: 'main.signalingConnecting' },
  connected: { color: 'bg-ok', labelKey: 'main.signalingConnected' },
  reconnecting: { color: 'bg-warn', labelKey: 'main.signalingReconnecting' },
  revoked: { color: 'bg-bad', labelKey: 'main.signalingDown' },
};

// The on-avatar presence dot: subtler than the standalone PresenceDot (offline
// fades into the hairline colour, per the prototype's row discs).
const PRESENCE_BG: Record<PresenceStatus, string> = {
  app: 'bg-ok',
  reachable: 'bg-warn',
  offline: 'bg-edge',
};

const AVATAR_SIZE = 38;

// Inline stroke SVGs (2px round caps) — no emoji, matching TabLayout/InCall.
function Icon({ d, size = 19 }: { d: string; size?: number }): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

const ADD_FRIEND_D = 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21v-1a7 7 0 0 1 14 0v1M19 8v6M16 11h6';
const GEAR_D =
  'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z';
const PHONE_D =
  'M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.13.96.36 1.9.7 2.8a2 2 0 0 1-.45 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.27a2 2 0 0 1 2.1-.45c.9.34 1.84.57 2.8.7a2 2 0 0 1 1.7 2.03z';

const ICON_BTN =
  'flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] text-muted hover:bg-raised hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent';

function firstInitial(name: string): string {
  const ch = name.trim()[0];
  return ch ? ch.toUpperCase() : '?';
}

function SidebarAvatar({
  userId,
  avatarSeed,
  name,
  presence,
}: {
  userId: string;
  avatarSeed: number | undefined;
  name: string;
  presence: PresenceStatus;
}): ReactNode {
  return (
    <span className="relative shrink-0" style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}>
      {avatarSeed !== undefined ? (
        <MercuryAvatar
          seed={candidateSeed(userId, avatarSeed)}
          size={AVATAR_SIZE}
          className="rounded-full"
        />
      ) : (
        // No chosen avatar → a neutral initial disc (the list keeps a
        // consistent leading disc; we never invent mercury pixels for it).
        <span
          aria-hidden="true"
          className="flex h-full w-full items-center justify-center rounded-full bg-raised text-sm font-medium text-muted"
        >
          {firstInitial(name)}
        </span>
      )}
      <span
        aria-hidden="true"
        data-presence={presence}
        className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-surface ${PRESENCE_BG[presence]}`}
      />
    </span>
  );
}

interface SidebarRowProps {
  userId: string;
  avatarSeed: number | undefined;
  name: string;
  presence: PresenceStatus;
  selected: boolean;
  /** Recents second line (`describeCall · timestamp`); omitted for people rows. */
  sub?: string;
  /** Missed call → danger colour on the name and sub. */
  danger?: boolean;
  /** Blocked peer in Recents → struck-through name (they are known, not hidden). */
  struck?: boolean;
  onOpen: () => void;
  onCall: () => void;
  t: TFunc;
}

function SidebarRow({
  userId,
  avatarSeed,
  name,
  presence,
  selected,
  sub,
  danger,
  struck,
  onOpen,
  onCall,
  t,
}: SidebarRowProps): ReactNode {
  return (
    <li className="group relative">
      <button
        type="button"
        data-sidebar-row
        aria-label={t('contacts.openAria', { name })}
        aria-current={selected ? 'true' : undefined}
        onClick={onOpen}
        className={`flex w-full items-center gap-2.5 rounded-[10px] py-1.5 pl-2 pr-10 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
          selected ? 'bg-raised shadow-[inset_0_0_0_1px_#2E3340]' : 'hover:bg-raised'
        }`}
      >
        <SidebarAvatar userId={userId} avatarSeed={avatarSeed} name={name} presence={presence} />
        <span className="min-w-0 flex-1">
          <span
            className={`block truncate ${danger ? 'text-danger' : 'text-ink'} ${
              struck ? 'line-through' : ''
            }`}
          >
            {name}
          </span>
          {sub !== undefined && (
            <span className={`block truncate text-xs ${danger ? 'text-danger' : 'text-muted'}`}>
              {sub}
            </span>
          )}
        </span>
      </button>
      {/* Sibling (not nested) button — valid markup, revealed on row hover or
          when it itself takes keyboard focus. */}
      <button
        type="button"
        aria-label={t('contacts.callAria', { name })}
        onClick={onCall}
        className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-[9px] bg-raised text-muted opacity-0 transition-opacity hover:text-ink focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent group-hover:opacity-100"
      >
        <Icon d={PHONE_D} size={16} />
      </button>
    </li>
  );
}

function Section({
  title,
  badge,
  badgeAria,
  children,
}: {
  title: string;
  badge?: number;
  badgeAria?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <section aria-label={title}>
      <h2 className="flex items-center gap-2 px-2.5 pb-1.5 pt-3.5 text-[11px] font-semibold uppercase tracking-[0.09em] text-muted">
        {title}
        {badge !== undefined && badge > 0 && (
          <span
            data-testid="missed-badge"
            aria-label={badgeAria}
            className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-[9px] bg-danger px-1.5 text-[11px] font-semibold text-ink"
          >
            {badge}
          </span>
        )}
      </h2>
      <ul className="flex flex-col gap-0.5">{children}</ul>
    </section>
  );
}

export function Sidebar(): ReactNode {
  const t = useT();
  const { api, engine, liveEvents } = useScreens();
  const navigate = useNavigate();
  const location = useLocation();

  const engineState = useStore(engine.state);
  const live = useStore(liveEvents.state);
  const favorites = useFavorites();
  const calls = useCallLog();
  const aliases = useAliases();
  const { dial } = useDialer();
  const updateWaiting = updateIsWaiting(useStore(appUpdateStore));

  const [friends, setFriends] = useState<Friend[]>(() => getCachedFriends()?.data ?? []);
  const [blocked, setBlocked] = useState<string[]>(() => getCachedBlocks()?.data ?? []);
  const [query, setQuery] = useState('');

  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Identical refresh to MainScreen: friends + blocks, cache both, tolerate an
  // offline failure by keeping the last known list (offline-first, no modal).
  const reload = useCallback(async () => {
    try {
      const res = await api.listFriends();
      setFriends(res.friends);
      setCachedFriends(res.friends);
    } catch {
      // keep last known friends
    }
    try {
      const res = await api.listBlocks();
      setBlocked(res.blocked);
      setCachedBlocks(res.blocked);
    } catch {
      // keep last known blocked set
    }
  }, [api]);
  useServerReload(reload);

  // `/` or Ctrl/Cmd+K focuses search (docs plan P4 §10). `/` is ignored while
  // typing in a field so it stays a literal slash there.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      const el = e.target as HTMLElement | null;
      const typing =
        el != null &&
        (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      } else if (e.key === '/' && !typing && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const presenceFor = (friend: Friend): PresenceStatus =>
    presenceOf(live, friend.userId, friend);
  const nameOf = (entry: (typeof calls)[number]): string =>
    displayNameFor(entry.peerUserId, friends, entry.peerName);

  const sections = buildSidebar({
    friends,
    favoriteIds: favorites,
    blocked,
    calls,
    query,
    presenceFor,
    nameOf,
    aliases,
  });

  const selectedUserId = matchPath('/contact/:userId', location.pathname)?.params.userId;

  function openContact(userId: string): void {
    void navigate(`/contact/${userId}`);
  }
  // A recents row is the sidebar's "open Recents" gesture — clearing the unseen
  // badge is a local operation with no server round-trip (matches MainScreen).
  function openFromRecents(userId: string): void {
    void markAllMissedSeen();
    openContact(userId);
  }
  function call(userId: string): void {
    void dial(userId);
  }

  // Roving focus: Arrow keys move focus between the row buttons in DOM order.
  // Enter/Space activate natively (they are buttons). Querying the DOM avoids
  // threading a ref array through every section.
  function moveFocus(dir: 1 | -1): void {
    const rows = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>('[data-sidebar-row]') ?? [],
    );
    if (rows.length === 0) return;
    const current = rows.indexOf(document.activeElement as HTMLElement);
    const next =
      current < 0 ? (dir === 1 ? 0 : rows.length - 1) : Math.min(Math.max(current + dir, 0), rows.length - 1);
    rows[next]?.focus();
  }
  function onListKeyDown(e: React.KeyboardEvent): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveFocus(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveFocus(-1);
    }
  }
  function onSearchKeyDown(e: React.KeyboardEvent): void {
    // ArrowDown drops from the search box into the first row.
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveFocus(1);
    }
  }

  const sig = engineState.signaling;

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="sidebar">
      <div className="flex items-center gap-2 px-4 pb-2.5 pt-3.5">
        <h1 className="text-[15px] font-semibold tracking-[0.14em]">MERKURY</h1>
        <span
          role="status"
          aria-label={t('main.serverConnection', { label: t(SIGNALING_DOT[sig].labelKey) })}
          title={t('main.serverSignaling')}
          className={`inline-block h-[7px] w-[7px] rounded-full ${SIGNALING_DOT[sig].color}`}
        />
        <span className="flex-1" />
        <button
          type="button"
          aria-label={t('contacts.addFriendAria')}
          onClick={() => void navigate('/add')}
          className={ICON_BTN}
        >
          <Icon d={ADD_FRIEND_D} />
        </button>
        <button
          type="button"
          aria-label={t('tabs.settings')}
          onClick={() => void navigate('/settings')}
          className={`relative ${ICON_BTN}`}
        >
          <Icon d={GEAR_D} />
          {updateWaiting && (
            <span
              data-testid="update-waiting-dot"
              className="absolute right-1 top-1 h-2 w-2 rounded-full bg-accent"
            />
          )}
        </button>
      </div>

      <div className="relative px-3 pb-2.5">
        <span className="pointer-events-none absolute left-6 top-1/2 -translate-y-1/2 text-muted">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
        </span>
        <input
          ref={searchRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onSearchKeyDown}
          aria-label={t('sidebar.searchAria')}
          placeholder={t('sidebar.searchPlaceholder')}
          autoComplete="off"
          className="h-9 w-full rounded-[10px] border border-transparent bg-raised pl-9 pr-3 text-ink placeholder:text-muted focus:border-accent focus:outline-none"
        />
      </div>

      {/* Push-blocked contexts (notifications denied on desktop) get the
          dismissible hint here; renders nothing when calls can ring. */}
      <div className="px-3">
        <InstallHint />
      </div>

      <div
        ref={listRef}
        onKeyDown={onListKeyDown}
        data-testid="sidebar-list"
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-3"
      >
        {sections.favorites.length > 0 && (
          <Section title={t('main.favorites')}>
            {sections.favorites.map((f) => (
              <SidebarRow
                key={f.userId}
                userId={f.userId}
                avatarSeed={f.avatarSeed}
                name={effectiveName(f, aliases)}
                presence={presenceFor(f)}
                selected={selectedUserId === f.userId}
                onOpen={() => openContact(f.userId)}
                onCall={() => call(f.userId)}
                t={t}
              />
            ))}
          </Section>
        )}

        {sections.contacts.length > 0 && (
          <Section title={t('contacts.title')}>
            {sections.contacts.map((f) => (
              <SidebarRow
                key={f.userId}
                userId={f.userId}
                avatarSeed={f.avatarSeed}
                name={effectiveName(f, aliases)}
                presence={presenceFor(f)}
                selected={selectedUserId === f.userId}
                onOpen={() => openContact(f.userId)}
                onCall={() => call(f.userId)}
                t={t}
              />
            ))}
          </Section>
        )}

        {sections.recents.length > 0 && (
          <Section
            title={t('main.recents')}
            badge={sections.missedUnseen}
            badgeAria={t('main.missedBadge', { count: sections.missedUnseen })}
          >
            {sections.recents.map((entry) => {
              const name = nameOf(entry);
              const friend = friends.find((f) => f.userId === entry.peerUserId);
              return (
                <SidebarRow
                  key={entry.id}
                  userId={entry.peerUserId}
                  avatarSeed={friend?.avatarSeed}
                  name={name}
                  presence={friend ? presenceFor(friend) : 'offline'}
                  selected={selectedUserId === entry.peerUserId}
                  sub={`${describeCall(entry)} · ${formatTimestamp(entry.startedAt)}`}
                  danger={entry.outcome === 'missed'}
                  struck={blocked.includes(entry.peerUserId)}
                  onOpen={() => openFromRecents(entry.peerUserId)}
                  onCall={() => call(entry.peerUserId)}
                  t={t}
                />
              );
            })}
          </Section>
        )}

        {sections.noRows && query.trim() !== '' && (
          <p className="px-3 pt-4 text-sm text-muted" data-testid="sidebar-no-matches">
            {t('sidebar.noMatches')}
          </p>
        )}
      </div>
    </div>
  );
}
